import { ChatInterface, ChatViewState, PromptTurn } from '@type/chat';
import { Message, MessageInterface, Role } from '@type/provider';

import { generateUUID } from './uuid';

const ROLE_SET = new Set<Role>(['user', 'assistant', 'system']);

const isRole = (value: unknown): value is Role =>
  typeof value === 'string' && ROLE_SET.has(value as Role);

export const createMessage = (role: Role, content = ''): MessageInterface => ({
  id: generateUUID(),
  role,
  content,
});

export const normalizeMessage = (
  message: Partial<MessageInterface> | Message
): MessageInterface => {
  const normalizedRole = isRole(message.role) ? message.role : 'user';
  const normalizedContent = typeof message.content === 'string' ? message.content : '';
  const currentId = 'id' in message ? message.id : undefined;

  return {
    ...message,
    id: typeof currentId === 'string' && currentId ? currentId : generateUUID(),
    role: normalizedRole,
    content: normalizedContent,
  };
};

const normalizeChatViewState = (
  viewState: ChatInterface['viewState'],
  validMessageIds: Set<string>
): ChatViewState | undefined => {
  if (!viewState) {
    return undefined;
  }

  const nextViewState: ChatViewState = {
    scrollTop: typeof viewState.scrollTop === 'number' ? viewState.scrollTop : 0,
  };

  if (
    typeof viewState.anchorMessageId === 'string' &&
    validMessageIds.has(viewState.anchorMessageId)
  ) {
    nextViewState.anchorMessageId = viewState.anchorMessageId;
  }

  if (
    typeof viewState.activeTurnStartMessageId === 'string' &&
    validMessageIds.has(viewState.activeTurnStartMessageId)
  ) {
    nextViewState.activeTurnStartMessageId = viewState.activeTurnStartMessageId;
  }

  return nextViewState;
};

export const normalizeChat = (chat: ChatInterface): ChatInterface => {
  const { viewState: _viewState, ...restChat } = chat;
  const seenMessageIds = new Set<string>();

  const normalizedMessages = (Array.isArray(chat.messages) ? chat.messages : []).map((message) => {
    const normalizedMessage = normalizeMessage(message);

    if (seenMessageIds.has(normalizedMessage.id)) {
      const deduplicatedMessage = { ...normalizedMessage, id: generateUUID() };
      seenMessageIds.add(deduplicatedMessage.id);
      return deduplicatedMessage;
    }

    seenMessageIds.add(normalizedMessage.id);
    return normalizedMessage;
  });

  const validMessageIds = new Set(normalizedMessages.map((message) => message.id));
  const normalizedViewState = normalizeChatViewState(chat.viewState, validMessageIds);

  return {
    ...restChat,
    id: typeof chat.id === 'string' && chat.id ? chat.id : generateUUID(),
    messages: normalizedMessages,
    titleSet: Boolean(chat.titleSet),
    timestamp: typeof chat.timestamp === 'number' ? chat.timestamp : Date.now(),
    ...(normalizedViewState ? { viewState: normalizedViewState } : {}),
  };
};

export const normalizeChats = (chats: ChatInterface[] = []): ChatInterface[] => {
  const seenChatIds = new Set<string>();

  return chats.map((chat) => {
    const normalizedChat = normalizeChat(chat);

    if (seenChatIds.has(normalizedChat.id)) {
      const deduplicatedChat = {
        ...normalizedChat,
        id: generateUUID(),
      };

      seenChatIds.add(deduplicatedChat.id);
      return deduplicatedChat;
    }

    seenChatIds.add(normalizedChat.id);
    return normalizedChat;
  });
};

const toPreview = (content: string, maxLength = 48): string => {
  const collapsed = content.replace(/\s+/g, ' ').trim();

  if (!collapsed) {
    return 'Empty prompt';
  }

  return collapsed.length > maxLength
    ? `${collapsed.slice(0, maxLength - 1).trimEnd()}…`
    : collapsed;
};

export const derivePromptTurns = (messages: MessageInterface[]): PromptTurn[] => {
  const userMessageIndexes = messages.reduce<number[]>((indexes, message, index) => {
    if (message.role === 'user') {
      indexes.push(index);
    }

    return indexes;
  }, []);

  return userMessageIndexes.map((startIndex, turnIndex) => {
    const endIndex =
      userMessageIndexes[turnIndex + 1] !== undefined
        ? userMessageIndexes[turnIndex + 1]! - 1
        : messages.length - 1;
    const startMessage = messages[startIndex]!;

    return {
      id: startMessage.id,
      startMessageId: startMessage.id,
      startIndex,
      endIndex,
      label: `Turn ${turnIndex + 1}`,
      preview: toPreview(startMessage.content),
    };
  });
};

export const findTopVisibleMessageId = (container: HTMLElement | null): string | undefined => {
  if (!container) {
    return undefined;
  }

  const containerTop = container.getBoundingClientRect().top;
  const messageElements = Array.from(
    container.querySelectorAll<HTMLElement>('[data-message-id]')
  );

  const firstVisibleMessage = messageElements.find(
    (element) => element.getBoundingClientRect().bottom > containerTop + 8
  );

  return firstVisibleMessage?.dataset.messageId;
};

export const findActiveTurnStartMessageId = (
  container: HTMLElement | null,
  turns: PromptTurn[],
  fallbackMessageId?: string
): string | undefined => {
  if (!container || turns.length === 0) {
    return undefined;
  }

  const containerTop = container.getBoundingClientRect().top;
  const turnStartElements = Array.from(
    container.querySelectorAll<HTMLElement>('[data-turn-start="true"]')
  );

  if (turnStartElements.length === 0) {
    return fallbackMessageId ?? turns[0]?.startMessageId;
  }

  let activeTurnStartMessageId =
    fallbackMessageId && turns.some((turn) => turn.startMessageId === fallbackMessageId)
      ? fallbackMessageId
      : turns[0]?.startMessageId;

  for (const element of turnStartElements) {
    const turnStartMessageId = element.dataset.messageId;

    if (!turnStartMessageId) {
      continue;
    }

    if (element.getBoundingClientRect().top - containerTop <= 120) {
      activeTurnStartMessageId = turnStartMessageId;
    } else {
      break;
    }
  }

  return activeTurnStartMessageId;
};